/**
 * Toast notification types — consumed by `useNotificationStore()`.
 *
 * Driver model: every notification routes through ONE of two
 * renderers:
 *
 *   `'pv'`     — PrimeVue's `<Toast />` (rendered with our custom
 *                `#container` slot so the visual chrome stays
 *                consistent with the native renderer). Default.
 *   `'native'` — our custom card stack rendered by the
 *                `<Notification />` layout partial.
 *
 * Both drivers route through the SAME store (so the in-memory entry
 * list + auto-dismiss timers + pause-on-hover + progress bar are
 * driver-independent). The driver only decides where the visual
 * mounts. Per-call override via `AlertOptions.driver` flips a single
 * invocation without mutating the store-wide default.
 *
 * Body is text-only. An HTML/`v-html` variant existed earlier but
 * was dropped — every caller-sanitized HTML path is an XSS surface
 * in waiting. Reintroduce via DOMPurify if a future caller needs
 * rich content.
 */

export enum NotificationPosition {
  TOP_LEFT = 'top-left',
  TOP_RIGHT = 'top-right',
  TOP_CENTER = 'top-center',
  BOTTOM_LEFT = 'bottom-left',
  BOTTOM_RIGHT = 'bottom-right',
  BOTTOM_CENTER = 'bottom-center',
}

export enum NotificationType {
  SUCCESS = 'success',
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warning',
}

/**
 * Which visual renderer mounts the notification.
 *   - `'pv'`     PrimeVue `<Toast />` (default — slick built-in
 *                animations + position handling).
 *   - `'native'` Custom card stack rendered by `<Notification />`.
 */
export type NotificationDriver = 'pv' | 'native'

/**
 * Per-call alert options. Anything omitted falls back to store
 * defaults (driver = current store driver; timeout = severity-
 * specific default).
 */
export interface AlertOptions {
  /**
   * One-time driver override for THIS call. Does NOT change the
   * store-wide default — leave `useNotificationStore().setDriver`
   * for that.
   */
  driver?: NotificationDriver
  /** Auto-dismiss in ms. Set to 0 to require manual dismiss. */
  timeout?: number
}

export interface SiteNotification {
  id: string
  type: NotificationType
  title?: string
  text?: string
  /** Which renderer is currently mounting this entry. */
  driver: NotificationDriver
  /** Milliseconds before auto-dismiss. Use 0 to require manual dismiss. */
  timeout?: number
  /** `Date.now()` at last (re)start of the auto-dismiss timer. */
  startedAt?: number
  /** Remaining ms while paused (hover). Resumes from this value. */
  remaining?: number
  /** setTimeout handle for dismissal. */
  timer?: number
  /** When true, the CSS progress animation is frozen via class binding. */
  paused?: boolean
}
